import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IGetDishInfoV2, IItemForGetShortDishInfo } from 'types';
import { modificationsModalInitialState, ModificationsModalContext } from './modificationsModalContext';
import { modificationsModalReducer } from './modificationsModalReducer';
import { ModificationsModalActionTypes } from './modificationsModalActions';
import { useFetchData } from 'hooks/useFetchData';
import { getDishInfoV2 } from 'backend/dishes';

export const ModificationsModalState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(modificationsModalReducer, modificationsModalInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const dishID = params.get('dishID');

	const { response, loading } = useFetchData<IGetDishInfoV2, string>(dishID ? getDishInfoV2 : null, dishID || '');

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ModificationsModalActionTypes.SET_ALL_DATA,
				payload: {
					dishInfo: response.data,
					IDsSelectedRestaurant: response.data.restaurants.map((restaurant) => restaurant.id),
					fetchedModificationsDishes: response.data.dishModifications.map((dishModification) => ({
						...dishModification,
						externalId: null,
					})),
					selectedModificationsDishes: response.data.dishModifications.map(
						(dishModification) => dishModification.id,
					),
				},
			});
		}
	}, [response]);

	const setModificationsDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: ModificationsModalActionTypes.SET_MODIFICATIONS_DISH_CATEGORY,
			payload: {
				selectedModificationsDishCategory: value,
			},
		});
	}, []);

	const setModificationsDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: ModificationsModalActionTypes.SET_MODIFICATIONS_DISH_SUB_CATEGORY,
			payload: {
				selectedModificationsDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedModificationsDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedModificationsDishes.includes(el.id));
			const filteredFetchedModificationsDishes = state.fetchedModificationsDishes.filter((el) =>
				state.selectedModificationsDishes.includes(el.id),
			);

			dispatch({
				type: ModificationsModalActionTypes.SET_FETCHED_MODIFICATIONS_DISHES,
				payload: {
					fetchedModificationsDishes: [...filteredValue, ...filteredFetchedModificationsDishes],
				},
			});
		},
		[state.selectedModificationsDishes, state.fetchedModificationsDishes],
	);

	const setModificationsDishes = useCallback((value: string[]) => {
		dispatch({
			type: ModificationsModalActionTypes.SET_MODIFICATIONS_DISHES,
			payload: {
				selectedModificationsDishes: value,
			},
		});
	}, []);

	const removeModificationsDish = useCallback(
		(value: string) => {
			const updatedSelectedModificationsDishes = state.selectedModificationsDishes.filter((el) => el !== value);

			dispatch({
				type: ModificationsModalActionTypes.SET_MODIFICATIONS_DISHES,
				payload: {
					selectedModificationsDishes: updatedSelectedModificationsDishes,
				},
			});
		},
		[state.selectedModificationsDishes],
	);

	return (
		<ModificationsModalContext.Provider
			value={{
				setModificationsDishCategory,
				setModificationsDishSubCategory,
				setFetchedModificationsDishes,
				setModificationsDishes,
				removeModificationsDish,
				dishInfo: state.dishInfo,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedModificationsDishCategory: state.selectedModificationsDishCategory,
				selectedModificationsDishSubCategory: state.selectedModificationsDishSubCategory,
				fetchedModificationsDishes: state.fetchedModificationsDishes,
				selectedModificationsDishes: state.selectedModificationsDishes,
			}}>
			{children}
		</ModificationsModalContext.Provider>
	);
};
